/**
 * Kütüphaneler ekranı — mobil gezinmenin merkezi.
 *
 * ## Neden ayrı bir ekran?
 *
 * Masaüstünde kütüphane listesi kenar çubuğunda durur (`Sidebar.tsx`).
 * Kenar çubuğu 900 px altında `display:none` olduğu için mobilde **hiçbir
 * yoldan** erişilemiyordu: alt bar yalnız 5 sekme sunuyordu ve "Kütüphane
 * Ekle" eylemi kayboluyordu.
 *
 * Karar (Tasarım V2, Karar 1): mobilde çizim (drawer) yerine **tam ekran bir
 * sayfa** kullanılır. Sayfa modeli seçildi çünkü:
 *
 *   - Tarayıcı geri düğmesi doğal çalışır (drawer'da elle kapatmak gerekir).
 *   - Ekran okuyucu için `main` landmark'ı içinde kalır.
 *   - Derin bağlantı verilebilir: `#/kutuphaneler`.
 *
 * Ekran hem mobil hem masaüstünde kullanılabilir; masaüstünde kenar çubuğu
 * varken ek bir gezinme yüzeyi olarak çalışır (aynı veri, iki sunum).
 */

import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import type { Library } from '../api.ts';
import { Empty, Loading } from '../components/States.tsx';
import {
  ArrowRightIcon,
  FilmIcon,
  FolderIcon,
  LockIcon,
  MusicIcon,
  PlusIcon,
  SearchIcon,
  TvIcon,
} from '../Icon.tsx';

interface LibraryKindMeta {
  icon: ReactNode;
  themeClass: string;
  label: string;
}

/**
 * Kütüphane türü → ikon + tema sınıfı.
 *
 * `HomeScreen` ile aynı karar: kütüphane adı, tür alanından daha güvenilir
 * bir sinyal (katalog adı "Diziler" ama `kind` "show" olabilir).
 */
function getLibraryMeta(library: Library): LibraryKindMeta {
  const lower = library.name.toLowerCase();
  if (
    lower.includes('müzik') ||
    lower.includes('muzik') ||
    lower.includes('music') ||
    library.kind === 'music'
  ) {
    return {
      icon: <MusicIcon size={20} />,
      themeClass: 'lib-browser__item--music',
      label: 'Müzik',
    };
  }
  if (library.kind === 'movie' || lower.includes('film')) {
    return { icon: <FilmIcon size={20} />, themeClass: 'lib-browser__item--movie', label: 'Film' };
  }
  if (library.kind === 'show' || lower.includes('dizi') || lower.includes('series')) {
    return { icon: <TvIcon size={20} />, themeClass: 'lib-browser__item--show', label: 'Dizi' };
  }
  return {
    icon: <FolderIcon size={20} />,
    themeClass: 'lib-browser__item--default',
    label: 'Medya',
  };
}

export function LibrariesScreen({
  libraries,
  canAddLibrary = false,
  onOpenLibrary,
  onAddLibrary,
  onOpenLiveTv,
}: {
  libraries: Library[] | null;
  /** Yalnız yönetici rolü "Kütüphane Ekle" eylemini görebilir. */
  canAddLibrary?: boolean;
  onOpenLibrary: (libraryId: string) => void;
  onAddLibrary?: () => void;
  /** Canlı TV ekranını aç. */
  onOpenLiveTv?: () => void;
}): ReactNode {
  const [query, setQuery] = useState('');

  /*
   * Arama sonucu **saf bir türetmedir**; durum tutulmaz, arama metni değiştiği
   * anda yeniden hesaplanır. `useMemo` yalnız liste uzunluğu değiştiğinde
   * yeniden hesaplanması için.
   */
  const filtered = useMemo(() => {
    if (!libraries) return [];
    const needle = query.trim().toLocaleLowerCase('tr-TR');
    if (needle.length === 0) return libraries;
    return libraries.filter((library) => library.name.toLocaleLowerCase('tr-TR').includes(needle));
  }, [libraries, query]);

  return (
    <div className="lib-browser">
      <header className="lib-browser__header">
        <div className="lib-browser__title-row">
          <h1 className="lib-browser__title">Kütüphaneler</h1>
          {libraries !== null && libraries.length > 0 && (
            <span className="section__badge">{libraries.length} kütüphane</span>
          )}
        </div>

        {libraries !== null && libraries.length > 0 && (
          <div className="lib-browser__search">
            <span className="lib-browser__search-icon" aria-hidden="true">
              <SearchIcon size={16} />
            </span>
            <input
              type="search"
              className="lib-browser__search-input"
              placeholder="Kütüphane ara…"
              aria-label="Kütüphanelerde ara"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        )}
      </header>

      {libraries === null && <Loading label="Kütüphaneler okunuyor…" />}

      {libraries !== null && libraries.length === 0 && (
        <Empty message="Henüz kütüphane tanımlanmadı." />
      )}

      {libraries !== null && libraries.length > 0 && filtered.length === 0 && (
        <Empty message={`"${query}" için eşleşen kütüphane yok.`} />
      )}

      {filtered.length > 0 && (
        <ul className="lib-browser__list">
          {filtered.map((library) => {
            const meta = getLibraryMeta(library);
            return (
              <li key={library.id}>
                <button
                  type="button"
                  className={`lib-browser__item ${meta.themeClass} ${
                    library.reachable ? '' : 'lib-browser__item--down'
                  }`}
                  onClick={() => onOpenLibrary(library.id)}
                  title={
                    library.reachable
                      ? undefined
                      : `Erişilemiyor: ${library.unavailableReason ?? 'bilinmiyor'}`
                  }
                >
                  <span className="lib-browser__item-icon" aria-hidden="true">
                    {meta.icon}
                  </span>

                  <span className="lib-browser__item-body">
                    <span className="lib-browser__item-name">{library.name}</span>
                    <span className="lib-browser__item-meta">
                      <span className="lib-browser__item-kind">{meta.label}</span>
                      <span aria-hidden="true">·</span>
                      <span>{library.itemCount} içerik</span>
                      {library.layer === 'cold' && (
                        <span className="badge badge--cold">
                          <LockIcon size={11} />
                          Tünel
                        </span>
                      )}
                      {!library.reachable && (
                        <span className="badge badge--down">Erişilemiyor</span>
                      )}
                    </span>
                  </span>

                  <span className="lib-browser__item-arrow" aria-hidden="true">
                    <ArrowRightIcon size={16} />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {onOpenLiveTv && (
        <div className="lib-browser__actions">
          <button
            type="button"
            className="button button--primary lib-browser__add"
            onClick={onOpenLiveTv}
          >
            <TvIcon size={16} />
            <span>Canlı TV</span>
          </button>
        </div>
      )}

      {canAddLibrary && onAddLibrary && (
        <div className="lib-browser__actions">
          <button
            type="button"
            className="button button--primary lib-browser__add"
            onClick={onAddLibrary}
          >
            <PlusIcon size={16} />
            <span>Kütüphane Ekle</span>
          </button>
        </div>
      )}
    </div>
  );
}
